import { useOnlineStatus } from "@ths/shared";
import { useEffect, useState } from "react";
import type { FC, FormEvent } from "react";

import {
  GetSelfCheckinReservationDocument,
  useResendBasecampEmailVerificationMutation,
  useUpdateBasecampEmailMutation,
  type GetSelfCheckinReservationQuery,
} from "../generated/graphql";

import Spinner from "./Spinner";

type Mode = "idle" | "editing" | "sent";
type Pending = "save" | "resend" | null;

interface EmailVerificationPanelProps {
  token: string;
  guestEmail: string | null;
  emailVerified: boolean;
}

const EmailVerificationPanel: FC<EmailVerificationPanelProps> = ({
  token,
  guestEmail,
  emailVerified,
}) => {
  const online = useOnlineStatus();
  const [mode, setMode] = useState<Mode>("idle");
  const [pending, setPending] = useState<Pending>(null);
  const [draftEmail, setDraftEmail] = useState(guestEmail ?? "");
  const [error, setError] = useState("");

  const [resend] = useResendBasecampEmailVerificationMutation();
  const [update] = useUpdateBasecampEmailMutation();

  // Auto-clear "sent" pill after 60s back to idle. The 60s mirrors the server
  // cooldown so the user can resend again exactly when the server allows.
  useEffect(() => {
    if (mode !== "sent") return;
    const t = setTimeout(() => setMode("idle"), 60_000);
    return () => clearTimeout(t);
  }, [mode]);

  if (emailVerified) return null;
  if (!guestEmail) return null;

  const handleResend = async () => {
    setError("");
    setPending("resend");
    try {
      await resend({ variables: { token } });
      setMode("sent");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't resend.");
    } finally {
      setPending(null);
    }
  };

  const handleStartEdit = () => {
    setError("");
    setDraftEmail(guestEmail);
    setMode("editing");
  };

  const handleCancelEdit = () => {
    setError("");
    setMode("idle");
  };

  const handleSave = async (e?: FormEvent) => {
    e?.preventDefault();
    const trimmed = draftEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Enter a valid email address.");
      return;
    }
    setError("");
    setPending("save");
    try {
      await update({
        variables: { token, email: trimmed },
        update: (cache) => {
          const existing = cache.readQuery<GetSelfCheckinReservationQuery>({
            query: GetSelfCheckinReservationDocument,
            variables: { token },
          });
          if (existing?.selfCheckinReservation) {
            cache.writeQuery<GetSelfCheckinReservationQuery>({
              query: GetSelfCheckinReservationDocument,
              variables: { token },
              data: {
                __typename: "Query",
                selfCheckinReservation: {
                  ...existing.selfCheckinReservation,
                  guestEmail: trimmed,
                  emailVerified: false,
                },
              },
            });
          }
        },
      });
      setMode("sent");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't update email.");
    } finally {
      setPending(null);
    }
  };

  const isPending = pending !== null;
  const disabled = !online || isPending || mode === "sent";

  return (
    <div className="bc-fade-up rounded-2xl border border-amber-500/20 bg-amber-500/[0.06] px-4 py-3.5">
      {mode === "editing" ? (
        <form onSubmit={handleSave} className="space-y-3">
          <label
            htmlFor="bc-verify-email"
            className="block text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300/80"
          >
            Your Email
          </label>
          <input
            id="bc-verify-email"
            type="email"
            value={draftEmail}
            onChange={(e) => setDraftEmail(e.target.value)}
            placeholder="name@example.com"
            className="w-full rounded-xl border border-white/[0.08] bg-white/[0.03] p-3 text-center text-sm font-medium text-white outline-none placeholder:text-white/15 focus:border-accentGreen/40"
          />
          {error && <p className="text-center text-xs text-red-300">{error}</p>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleCancelEdit}
              disabled={pending === "save"}
              className="flex-1 rounded-xl border border-white/[0.08] py-2 text-xs font-semibold text-white/60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!online || pending === "save"}
              className="flex flex-1 items-center justify-center rounded-xl bg-accentGreen py-2 text-xs font-semibold text-primaryDarkBlue disabled:opacity-50"
            >
              {pending === "save" ? <Spinner /> : "Save"}
            </button>
          </div>
        </form>
      ) : (
        <>
          <p className="text-center text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300/80">
            {mode === "sent"
              ? "✓ Sent — check your inbox"
              : "Confirm your email"}
          </p>
          <p className="mt-1 truncate text-center text-sm font-medium text-amber-100/90">
            {guestEmail}
          </p>
          {error && (
            <p className="mt-2 text-center text-xs text-red-300">{error}</p>
          )}
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={handleResend}
              disabled={disabled}
              className="flex flex-1 items-center justify-center rounded-xl border border-amber-400/30 bg-amber-400/10 py-2 text-xs font-semibold text-amber-200 disabled:opacity-50"
            >
              {pending === "resend" ? <Spinner /> : "Resend"}
            </button>
            <button
              type="button"
              onClick={handleStartEdit}
              disabled={!online || isPending}
              className="flex-1 rounded-xl border border-white/[0.08] py-2 text-xs font-semibold text-white/60"
            >
              Change
            </button>
          </div>
          {!online && (
            <p className="mt-2 text-center text-[10px] text-white/30">
              Reconnect to manage your email.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default EmailVerificationPanel;
